'use client'

import { smartSRPMessages } from '@/src/utils/srp/aiRecommendation/locale'
import { useLocale } from 'next-intl'
import { useEffect, useMemo, useRef } from 'react'

const STORAGE_KEY = 'hasSeenSmartSortTour'
const POLL_INTERVAL_MS = 500
const MAX_POLL_ATTEMPTS = 30
const POST_ELEMENT_DELAY_MS = 600

export function useSmartSortTour () {
    const locale = useLocale() as 'en' | 'ar'
    const tourMessages = useMemo(() => smartSRPMessages[locale].tour, [locale])
    const destroyRef = useRef<(() => void) | null>(null)
    const hasRun = useRef(false)

    useEffect(() => {
        if (typeof window === 'undefined') return

        try {
            if (localStorage.getItem(STORAGE_KEY)) return
        } catch {
            return
        }

        if (hasRun.current) return

        const observer = new MutationObserver(async () => {
            const sortMessage = document.getElementById('smart-sort-message')
            if (!sortMessage) return

            observer.disconnect()
            if (hasRun.current) return
            hasRun.current = true

            await new Promise(r => setTimeout(r, POST_ELEMENT_DELAY_MS))

            let metricsPollCount = 0
            await new Promise<void>(resolve => {
                const metricsPoll = setInterval(() => {
                    metricsPollCount++
                    if (document.getElementById('smart-metrics-badge') || metricsPollCount > MAX_POLL_ATTEMPTS) {
                        clearInterval(metricsPoll)
                        resolve()
                    }
                }, POLL_INTERVAL_MS)
            })

            const { driver } = await import('driver.js')
            // @ts-ignore CSS module import
            await import('driver.js/dist/driver.css')

            const steps: any[] = [{
                element: '#smart-sort-message',
                popover: {
                    title: tourMessages.sortMessage.title,
                    description: tourMessages.sortMessage.description,
                    side: 'bottom',
                    align: 'center'
                }
            }]

            if (document.getElementById('smart-metrics-badge')) {
                steps.push({
                    element: '#smart-metrics-badge',
                    popover: {
                        title: tourMessages.cardMetrics.title,
                        description: tourMessages.cardMetrics.description,
                        side: 'top',
                        align: 'center'
                    }
                })
            }

            const sortMessageEl = document.getElementById('smart-sort-message')
            if (sortMessageEl) {
                sortMessageEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
                await new Promise(r => setTimeout(r, 400))
            }

            const scrollY = window.scrollY

            const driverObj = driver({
                showProgress: steps.length > 1,
                showButtons: ['next', 'close'],
                smoothScroll: false,
                overlayColor: 'rgba(0, 0, 0, 0.5)',
                popoverClass: 'smart-sort-tour-popover',
                nextBtnText: locale === 'ar' ? 'التالي' : 'Next',
                doneBtnText: locale === 'ar' ? 'فهمت' : 'Got it',
                onHighlightStarted: () => {
                    requestAnimationFrame(() => window.scrollTo(0, scrollY))
                },
                onDestroyStarted: () => {
                    try {
                        localStorage.setItem(STORAGE_KEY, 'true')
                    } catch { /* privacy mode */ }
                    driverObj.destroy()
                },
                steps
            })

            destroyRef.current = () => driverObj.destroy()
            driverObj.drive()
            requestAnimationFrame(() => window.scrollTo(0, scrollY))
        })

        observer.observe(document.body, { childList: true, subtree: true })

        return () => {
            observer.disconnect()
            destroyRef.current?.()
            destroyRef.current = null
        }
    }, [tourMessages])
}
